import type { GammaMarket } from "@/api/polymarketApi";

export interface DeskOutcome {
	name: string;
	price: number | null;
	tokenId: string | null;
}

export interface DeskMarket {
	conditionId: string;
	question: string;
	slug: string | null;
	outcomes: DeskOutcome[];
	vol24: number;
	vol1w: number;
	vol1mo: number | null;
	volTotal: number;
	liquidity: number;
	change24: number | null;
	change1h: number | null;
	startDate: string | null;
	endDate: string | null;
	category: string | null;
	closed: boolean;
	/** 24h volume over the market's own daily baseline. Null when too new to compare. */
	volX: number | null;
	/** Daily volume before today, from the busier of the 7-day and 30-day windows. */
	baselineDaily: number | null;
	ageDays: number | null;
	/** Fixture kick-off, when Polymarket gives one. */
	gameStartMs: number | null;
	/** Sports or esports fixture: volume follows the calendar rather than news. */
	isGame: boolean;
	eventId: string | null;
	eventTitle: string | null;
	/** UMA oracle state, e.g. "proposed" or "disputed". */
	resolution: string | null;
	/** Priced at the extremes, so effectively decided even if not yet resolved. */
	settled: boolean;
}

const DAY_MS = 86_400_000;
const SETTLED_EDGE = 0.015;
const MIN_PRIOR_DAYS = 2;
const MIN_MONTH_DAYS = 7;
// Below this a baseline is noise, so it counts as this much. A market that traded $50 a
// day and then $20K reads 20×, not 400×.
export const MIN_BASELINE_USD = 1_000;

function parseList(v: unknown): string[] {
	if (Array.isArray(v)) return v.map(String);
	if (typeof v !== "string" || !v) return [];
	try {
		const parsed = JSON.parse(v);
		return Array.isArray(parsed) ? parsed.map(String) : [];
	} catch {
		return [];
	}
}

const finite = (v: unknown): number | null => {
	if (v === null || v === undefined || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
};

// First tag that matches, most specific first. Polymarket tags are free-form,
// so anything unmatched stays uncategorised rather than being guessed.
const CATEGORY_TAGS: [string, string[]][] = [
	["Sports", ["sports", "games", "soccer", "tennis", "esports", "nba", "nfl", "mlb", "nhl", "ufc"]],
	["Crypto", ["crypto", "bitcoin", "ethereum", "crypto prices"]],
	["Geopolitics", ["geopolitics", "world", "middle east", "ukraine"]],
	["Politics", ["politics", "elections", "us election", "global elections", "world elections"]],
	["Economy", ["economy", "finance", "fed rates", "business", "stocks"]],
	["Tech", ["tech", "ai"]],
	["Culture", ["culture", "pop culture", "movies", "music"]],
];

export function categoryFromTags(tags: { label?: string }[] | null | undefined): string | null {
	const labels = new Set((tags ?? []).map((t) => (t.label ?? "").toLowerCase()));
	for (const [name, keys] of CATEGORY_TAGS) {
		if (keys.some((k) => labels.has(k))) return name;
	}
	return null;
}

/**
 * Typical daily volume before today. Gamma only gives window totals, so there are no daily
 * values to take a median of. The robust choice left is the busier of two windows that do
 * not overlap: days 2-7 and days 8-30. One quiet week then cannot make a normal day look
 * huge. Null below MIN_PRIOR_DAYS + 1 days old: a naive volume24h / (volume7d / 7) gives
 * every new market 7.0.
 */
export function dailyBaseline(
	vol24: number,
	vol1w: number,
	ageDays: number | null,
	vol1mo: number | null = null,
): number | null {
	if (ageDays === null) return null;
	const age = Math.floor(ageDays);
	const weekDays = Math.min(6, age - 1);
	if (weekDays < MIN_PRIOR_DAYS) return null;
	const week = Math.max(0, vol1w - vol24) / weekDays;
	const monthDays = Math.min(23, age - 7);
	if (vol1mo === null || monthDays < MIN_MONTH_DAYS) return week;
	return Math.max(week, Math.max(0, vol1mo - vol1w) / monthDays);
}

/** 24h volume as a multiple of the baseline, which is floored at MIN_BASELINE_USD. */
export function volumeMultiple(
	vol24: number,
	vol1w: number,
	ageDays: number | null,
	vol1mo: number | null = null,
): number | null {
	const base = dailyBaseline(vol24, vol1w, ageDays, vol1mo);
	return base === null ? null : vol24 / Math.max(base, MIN_BASELINE_USD);
}

const timeMs = (v: string | null | undefined): number | null => {
	if (!v) return null;
	// gameStartTime arrives as "2026-11-04 17:45:00+00", which not every engine parses.
	const t = new Date(v.replace(" ", "T").replace(/\+00$/, "Z")).getTime();
	return Number.isFinite(t) ? t : null;
};

export function normalizeMarket(m: GammaMarket, nowMs = Date.now()): DeskMarket {
	const names = parseList(m.outcomes);
	const prices = parseList(m.outcomePrices);
	const tokens = parseList(m.clobTokenIds);
	const outcomes: DeskOutcome[] = names.map((name, i) => ({
		name,
		price: finite(prices[i]),
		tokenId: tokens[i] ?? null,
	}));
	const vol24 = finite(m.volume24hr) ?? 0;
	const vol1w = finite(m.volume1wk) ?? 0;
	const vol1mo = finite(m.volume1mo);
	const started = m.startDate ?? m.createdAt ?? null;
	const startMs = started ? new Date(started).getTime() : Number.NaN;
	const ageDays = Number.isFinite(startMs) ? Math.max(0, (nowMs - startMs) / DAY_MS) : null;
	const first = outcomes[0]?.price ?? null;
	const gameStartMs = timeMs(m.gameStartTime) ?? timeMs(m.eventStartTime);
	const event = m.events?.[0];
	return {
		conditionId: m.conditionId,
		question: m.question ?? m.conditionId,
		slug: m.slug ?? null,
		outcomes,
		vol24,
		vol1w,
		vol1mo,
		volTotal: finite(m.volumeNum ?? m.volume) ?? 0,
		liquidity: finite(m.liquidityNum ?? m.liquidity) ?? 0,
		change24: finite(m.oneDayPriceChange),
		change1h: finite(m.oneHourPriceChange),
		startDate: m.startDate ?? null,
		endDate: m.endDate ?? null,
		category: categoryFromTags(m.tags),
		closed: Boolean(m.closed),
		volX: volumeMultiple(vol24, vol1w, ageDays, vol1mo),
		baselineDaily: dailyBaseline(vol24, vol1w, ageDays, vol1mo),
		ageDays,
		gameStartMs,
		isGame: gameStartMs !== null || Boolean(m.sportsMarketType),
		eventId: event?.id ?? null,
		eventTitle: event?.title ?? null,
		resolution: m.umaResolutionStatus ?? null,
		settled: first !== null && (first <= SETTLED_EDGE || first >= 1 - SETTLED_EDGE),
	};
}

/** Case-insensitive match across any of the given fields. An empty query matches everything. */
export function matches(q: string, ...fields: (string | null | undefined)[]): boolean {
	const needle = q.trim().toLowerCase();
	if (!needle) return true;
	return fields.some((f) => f?.toLowerCase().includes(needle));
}

/** Options for a category filter. A value from a shared link stays selectable before data arrives. */
export function categoryOptions(names: string[], current: string): { value: string; label: string }[] {
	const list = current !== "all" && !names.includes(current) ? [...names, current] : names;
	return [{ value: "all", label: "Category: all" }, ...list.map((c) => ({ value: c, label: c }))];
}

export const marketUrl = (slug: string | null, conditionId: string): string =>
	slug ? `https://polymarket.com/market/${slug}` : `https://polymarket.com/search?q=${conditionId}`;
